import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import StatusBadge from "@/components/StatusBadge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import QboIntegrationPanel from "@/components/QboIntegrationPanel";
import PunchoutSettingsPanel from "@/components/PunchoutSettingsPanel";
import { fmtDateTime } from "@/lib/utils";
import SearchableSelect from "@/components/SearchableSelect";
import RampVendorPush from "@/components/RampVendorPush";
import { RampOwnAppConnect, RampOwnAppDelete } from "@/components/RampOwnAppSetup";

const INTEGRATION_META = {
  qbo: { name: "QuickBooks Online", desc: "Sync chart of accounts, vendors and job costs." },
  corpay: { name: "Corpay", desc: "Import card transactions and assign to jobs." },
  ramp: { name: "Ramp", desc: "Pulls Ramp card charges and receipts so they can be coded to jobs here (or matched to the Expense Log), imports your Ramp users, and pays vendor invoices with Ramp Bill Pay." },
};
const CARD_PLATFORM_TYPES = ["corpay", "ramp"];

export default function Settings() {
  const [company, setCompany] = useState(null);
  const [integrations, setIntegrations] = useState([]);
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [billability, setBillability] = useState("non_billable");
  const [markup, setMarkup] = useState(0);
  const [accountingMode, setAccountingMode] = useState("qbo");
  const [overBudgetBehavior, setOverBudgetBehavior] = useState("warn");
  const [apiKeyDrafts, setApiKeyDrafts] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();
  // Back from Ramp's / Intuit's sign-in: land on Integrations and say how it went.
  const [tab, setTab] = useState(searchParams.get("tab") || (searchParams.get("ramp") || searchParams.get("qbo") ? "integrations" : "profile"));
  useEffect(() => {
    const rampResult = searchParams.get("ramp");
    const qboResult = searchParams.get("qbo");
    if (!rampResult && !qboResult) return;
    if (rampResult === "connected") toast.success("Ramp connected — card charges sync daily, or click Sync Now.");
    if (rampResult === "error") toast.error(`Ramp wasn't connected: ${searchParams.get("reason") || "something went wrong"}`, { duration: 9000 });
    if (qboResult === "connected") toast.success("QuickBooks connected");
    if (qboResult === "error") toast.error("QuickBooks wasn't connected — try Connect again.");
    setSearchParams({}, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = () => {
    api.get("/contractor/settings").then((r) => {
      setCompany(r.data);
      setName(r.data.name); setAddress(r.data.address || ""); setPhone(r.data.phone || "");
      setBillability(r.data.default_billability || "non_billable");
      setMarkup(r.data.default_markup_percent ?? 0);
      setAccountingMode(r.data.accounting_mode || "export_only");
      setOverBudgetBehavior(r.data.over_budget_behavior || "warn");
    });
    api.get("/contractor/integrations").then((r) => setIntegrations(r.data));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    await api.patch("/contractor/settings", { name, address, phone });
    toast.success("Company profile updated");
    load();
  };

  const saveDefaults = async () => {
    await api.patch("/contractor/settings", {
      default_billability: billability,
      default_markup_percent: Number(markup), accounting_mode: accountingMode,
      over_budget_behavior: overBudgetBehavior,
    });
    toast.success("Defaults & accounting settings updated");
    load();
  };

  const connectQbo = async () => {
    await api.post(`/contractor/integrations/qbo/connect`, {});
    toast.success("QuickBooks Online connected (mock sync)");
    load();
  };

  const disconnectIntegration = async (type) => {
    await api.post(`/contractor/integrations/${type}/disconnect`);
    toast.success(`${INTEGRATION_META[type].name} disconnected`);
    load();
  };

  const updateCapabilities = async (type, changes) => {
    try {
      await api.patch(`/contractor/integrations/${type}/capabilities`, changes);
      toast.success("Payment capabilities updated");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const connectCardPlatform = async (type) => {
    const apiKey = (apiKeyDrafts[type] || "").trim();
    if (!apiKey) {
      toast.error("An API key is required to connect.");
      return;
    }
    try {
      await api.post(`/contractor/integrations/${type}/connect`, { api_key: apiKey });
      toast.success(`${INTEGRATION_META[type].name} connected`);
      setApiKeyDrafts((d) => ({ ...d, [type]: "" }));
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail.map((d) => d.message).join(" ") : detail || "Failed to connect");
    }
  };

  const connectRamp = async () => {
    try {
      const { data } = await api.post("/contractor/integrations/ramp/authorize");
      window.location.href = data.url; // Ramp's sign-in; it sends the admin back here
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start Connect with Ramp");
    }
  };

  const setRampPoster = async (poster) => {
    try {
      const { data } = await api.patch("/contractor/integrations/ramp/qbo-poster", { qbo_poster: poster });
      toast.success(`${poster === "ramp" ? "Ramp" : "ScapeSource"} now posts Ramp card charges to QuickBooks${data.moved ? ` — ${data.moved} Expense Log entr${data.moved === 1 ? "y" : "ies"} moved` : ""}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const sync = async (type) => {
    const res = await api.post(`/contractor/integrations/${type}/sync`);
    const { imported, duplicates, failed, error } = res.data;
    if (res.data.running) {
      // Card platforms sync in the background — results land in Ramp Transactions.
      toast.info(res.data.already_running ? "A sync is already running." : "Sync started in the background — new charges appear under Ramp Transactions when it's done.");
    } else if (imported === undefined) {
      toast.success("Sync triggered");
    } else if (error) {
      toast.info(`Sync ran with a note: ${error}`);
    } else {
      toast.success(`Sync complete — ${imported} imported, ${duplicates} duplicate${duplicates === 1 ? "" : "s"}, ${failed} failed`);
    }
    load();
  };

  if (!company) return <div className="p-8 text-slate-400 text-sm">Loading…</div>;

  return (
    <div className="p-6 md:p-8 max-w-3xl" data-testid="settings-page">
      <h1 className="text-2xl font-extrabold tracking-tight">Settings</h1>
      <p className="text-slate-500 text-sm mt-1">Manage your company profile and integrations.</p>

      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList>
          <TabsTrigger value="profile" data-testid="tab-profile">Company Profile</TabsTrigger>
          <TabsTrigger value="defaults" data-testid="tab-defaults">Defaults &amp; Accounting</TabsTrigger>
          <TabsTrigger value="integrations" data-testid="tab-integrations">Integrations</TabsTrigger>
          <TabsTrigger value="punchout" data-testid="tab-punchout">Punchout</TabsTrigger>
        </TabsList>
        <TabsContent value="profile" className="mt-5 space-y-4">
          <div>
            <label className="text-[13px] font-medium text-slate-700">Company Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} data-testid="company-name-input" className="mt-1.5 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
          </div>
          <div>
            <label className="text-[13px] font-medium text-slate-700">Address</label>
            <input value={address} onChange={(e) => setAddress(e.target.value)} data-testid="company-address-input" className="mt-1.5 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
          </div>
          <div>
            <label className="text-[13px] font-medium text-slate-700">Phone</label>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="company-phone-input" className="mt-1.5 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
          </div>
          <button onClick={save} data-testid="save-settings-button" className="px-4 py-2.5 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-[13px] font-semibold">Save Changes</button>
        </TabsContent>
        <TabsContent value="defaults" className="mt-5 space-y-4">
          <div>
            <label className="text-[13px] font-medium text-slate-700">Default Billability</label>
            <SearchableSelect value={billability} onChange={(e) => setBillability(e.target.value)} data-testid="default-billability-select" className="mt-1.5 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm">
              <option value="non_billable">Non-billable</option>
              <option value="billable">Billable</option>
            </SearchableSelect>
          </div>
          <div>
            <label className="text-[13px] font-medium text-slate-700">Default Markup (%)</label>
            <input type="number" step="0.01" value={markup} onChange={(e) => setMarkup(e.target.value)} data-testid="default-markup-input" className="mt-1.5 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
          </div>
          <div>
            <label className="text-[13px] font-medium text-slate-700">Accounting Mode</label>
            <SearchableSelect value={accountingMode} onChange={(e) => setAccountingMode(e.target.value)} data-testid="accounting-mode-select" className="mt-1.5 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm">
              <option value="export_only">Export only (non-QBO)</option>
              <option value="qbo_light">QuickBooks Online — Light</option>
              <option value="qbo">QuickBooks Online</option>
            </SearchableSelect>
          </div>
          <div data-testid="budget-limit-note">
            <label className="text-[13px] font-medium text-slate-700">Job Budgets</label>
            <p className="text-[12px] text-slate-500 mt-1">
              A job's budget is a hard limit: no purchase, expense or approval can take what's left below $0 — for anyone.
              A vendor invoice that doesn't fit waits until the job's budget is raised. Jobs without a budget have no limit.
            </p>
          </div>
          <button onClick={saveDefaults} data-testid="save-defaults-button" className="px-4 py-2.5 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-[13px] font-semibold">Save Changes</button>
        </TabsContent>
        <TabsContent value="integrations" className="mt-5 space-y-3">
          {/* QBO gets the real OAuth/mapping panel (Package 17) — hidden in
              export_only mode, where the accounting export replaces it. */}
          {accountingMode !== "export_only" && <QboIntegrationPanel />}
          {integrations.filter((i) => i.type !== "qbo").map((i) => {
            const isCardPlatform = CARD_PLATFORM_TYPES.includes(i.type);
            const connected = i.status === "connected";
            return (
              <div key={i.type} className="border border-slate-200 rounded-xl p-4" data-testid={`integration-card-${i.type}`}>
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <div className="font-semibold text-[14px]">{INTEGRATION_META[i.type].name}</div>
                    <div className="text-[12.5px] text-slate-500 mt-0.5">{INTEGRATION_META[i.type].desc}</div>
                    {connected && i.api_key_masked && (
                      <div className="text-[11.5px] text-slate-400 mt-1 mono" data-testid={`api-key-masked-${i.type}`}>
                        API key: {i.api_key_masked}
                      </div>
                    )}
                    {connected && i.type === "ramp" && i.environment && (
                      <div className="text-[11.5px] text-slate-400 mt-1" data-testid="ramp-environment">
                        Connected to Ramp {i.environment === "production" ? "" : "sandbox"}{" "}
                        {i.auth === "client_credentials" ? `with your own Ramp app (Client ID ••••${i.client_id_hint}) — encrypted, can't be viewed` : "via Ramp sign-in"}
                      </div>
                    )}
                    {i.last_sync && <div className="text-[11.5px] text-slate-400 mt-1">Last sync: {fmtDateTime(i.last_sync)}</div>}
                  </div>
                  <div className="flex items-center gap-3">
                    <StatusBadge status={i.status} />
                    {connected && <button onClick={() => sync(i.type)} data-testid={`sync-${i.type}-button`} className="text-[12px] font-semibold text-sky-600">Sync Now</button>}
                    {/* A company's own Ramp app is changed only by deleting it permanently and adding again. */}
                    {(connected || i.status === "needs_reconnect") && i.type === "ramp" && i.auth === "client_credentials" ? (
                      <RampOwnAppDelete clientIdHint={i.client_id_hint} onDeleted={load} />
                    ) : connected && (
                      <button onClick={() => disconnectIntegration(i.type)} data-testid={`toggle-${i.type}-button`} className="px-3 py-1.5 rounded-lg border border-slate-200 text-[12px] font-semibold hover:bg-slate-50">
                        Disconnect
                      </button>
                    )}
                    {!connected && !isCardPlatform && (
                      <button onClick={connectQbo} data-testid={`toggle-${i.type}-button`} className="px-3 py-1.5 rounded-lg border border-slate-200 text-[12px] font-semibold hover:bg-slate-50">
                        Connect
                      </button>
                    )}
                  </div>
                </div>
                {connected && i.type === "ramp" && (
                  <div className="mt-3 pt-3 border-t border-slate-100 text-[12.5px] text-slate-600 space-y-1" data-testid="capabilities-ramp">
                    <div>Card charges &amp; receipts: <b>{(i.capabilities || {}).cards ? "On" : "Off"}</b> — synced daily, or Sync Now. They're matched on the Ramp Transactions page.</div>
                    <div data-testid="ramp-coding-capability">
                      Coding charges &amp; importing users: <b>{(i.capabilities || {}).coding && (i.capabilities || {}).users ? "On" : "Off"}</b>
                      {!((i.capabilities || {}).coding && (i.capabilities || {}).users) && (
                        <span className="text-slate-400"> — Ramp didn't grant the write and user permissions. Enable them in your Ramp app (Developer settings), then disconnect and connect Ramp again.</span>
                      )}
                    </div>
                    <div>
                      Bill Pay for vendor invoices: <b>{(i.capabilities || {}).bill_pay ? "On" : "Off"}</b>
                      {!(i.scopes || []).includes("bills:write") && <span className="text-slate-400"> — Ramp didn't grant Bill Pay access. Disconnect and connect again, approving Bill Pay in Ramp.</span>}
                      {(i.scopes || []).includes("bills:write") && (
                        <label className="ml-3 inline-flex items-center gap-1.5">
                          <input type="checkbox" checked={!!(i.capabilities || {}).bill_pay} onChange={(e) => updateCapabilities("ramp", { bill_pay: e.target.checked })} data-testid="capability-billpay-ramp" />
                          Offer "Pay with Ramp" on Open Invoices
                        </label>
                      )}
                    </div>
                    <div className="text-[11.5px] text-slate-400">Bills sent to Ramp never sync to QuickBooks from Ramp — ScapeSource posts the bill and its payment.</div>
                    <RampVendorPush />

                    <div className="pt-2 mt-2 border-t border-slate-100" data-testid="ramp-qbo-poster">
                      <div className="font-semibold text-slate-700">Who posts Ramp card charges to QuickBooks</div>
                      <div className="text-[11.5px] text-slate-500 mt-0.5">
                        Exactly one of them does, so a charge is never in QuickBooks twice.{" "}
                        {i.ramp_accounting_link == null
                          ? "ScapeSource can't see whether Ramp is connected to QuickBooks — reconnect Ramp to let it check."
                          : i.ramp_accounting_link.linked
                            ? `Ramp is connected to ${i.ramp_accounting_link.provider || "an accounting system"}.`
                            : "Ramp isn't connected to QuickBooks."}
                      </div>
                      <div className="mt-2 space-y-1.5">
                        <label className="flex items-start gap-2">
                          <input type="radio" name="ramp-poster" checked={i.qbo_poster === "scapesource"} onChange={() => setRampPoster("scapesource")} data-testid="ramp-poster-scapesource" className="mt-0.5" />
                          <span><b>ScapeSource</b> — Ramp-paid Expense Log entries sync from here, with their job and cost code. Turn off card-transaction sync to QuickBooks in Ramp.</span>
                        </label>
                        <label className="flex items-start gap-2">
                          <input type="radio" name="ramp-poster" checked={i.qbo_poster === "ramp"} onChange={() => setRampPoster("ramp")} data-testid="ramp-poster-ramp" className="mt-0.5" />
                          <span><b>Ramp</b> — Ramp's own QuickBooks sync posts them (code the job in Ramp). Here they still count in job cost and reports, but skip To sync.</span>
                        </label>
                      </div>
                      {i.qbo_poster === "scapesource" && i.ramp_accounting_link?.linked && (
                        <div className="mt-2 text-[12px] text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="ramp-double-warning">
                          Ramp is connected to {i.ramp_accounting_link.provider || "QuickBooks"} too. In Ramp, turn off syncing card transactions to QuickBooks — otherwise
                          each charge goes in twice. ScapeSource won't post a charge Ramp already posted, and flags any that Ramp posts afterwards.
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {connected && isCardPlatform && i.type !== "ramp" && (
                  <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-5 text-[12.5px] text-slate-600" data-testid={`capabilities-${i.type}`}>
                    <span className="font-semibold text-slate-500">Payment capabilities:</span>
                    <label className="flex items-center gap-1.5">
                      <input type="checkbox" checked={!!(i.capabilities || {}).cards}
                        onChange={(e) => updateCapabilities(i.type, { cards: e.target.checked })}
                        data-testid={`capability-cards-${i.type}`} />
                      Cards
                    </label>
                    <label className="flex items-center gap-1.5">
                      <input type="checkbox" checked={!!(i.capabilities || {}).bill_pay}
                        onChange={(e) => updateCapabilities(i.type, { bill_pay: e.target.checked })}
                        data-testid={`capability-billpay-${i.type}`} />
                      Bill pay (check / ACH)
                    </label>
                    <span className="text-[11.5px] text-slate-400">Controls which payment options appear on Open Invoices.</span>
                  </div>
                )}
                {!connected && i.type === "ramp" && i.status === "needs_reconnect" && i.auth === "client_credentials" && (
                  <div className="mt-3 pt-3 border-t border-slate-100 text-[12px] text-amber-700 font-medium" data-testid="ramp-own-app-rejected">
                    Ramp no longer accepts your saved Ramp app credentials (they may have been rotated or the app deleted). Delete them and add the new ones.
                  </div>
                )}
                {!connected && i.type === "ramp" && i.auth !== "client_credentials" && (
                  <div className="mt-3 pt-3 border-t border-slate-100 space-y-3" data-testid="ramp-connect">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="text-[12px] text-slate-500 max-w-xl">
                        {i.status === "needs_reconnect"
                          ? <span className="text-amber-700 font-medium">Ramp access expired or was revoked — connect again to keep syncing.</span>
                          : <>Create a small app in your own Ramp account and paste its keys here — a step-by-step guide walks you through it. Needs a Ramp admin.</>}
                      </div>
                      <RampOwnAppConnect onConnected={load} />
                    </div>
                    {i.available !== false && (
                      <div className="flex items-center justify-between gap-3 flex-wrap text-[12px] text-slate-500">
                        <span>Or sign in to Ramp and approve access (Ramp's one-click connection).</span>
                        <button onClick={connectRamp} data-testid="connect-ramp-button"
                          className="px-3 py-1.5 rounded-lg border border-slate-200 text-[12px] font-semibold hover:bg-slate-50 whitespace-nowrap">
                          {i.status === "needs_reconnect" ? "Reconnect Ramp" : "Connect with Ramp"}
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {!connected && isCardPlatform && i.type !== "ramp" && (
                  <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2">
                    <input
                      type="password" placeholder="API key" value={apiKeyDrafts[i.type] || ""}
                      onChange={(e) => setApiKeyDrafts((d) => ({ ...d, [i.type]: e.target.value }))}
                      data-testid={`api-key-input-${i.type}`}
                      className="flex-1 h-9 px-3 rounded-lg border border-slate-200 text-sm"
                    />
                    <button onClick={() => connectCardPlatform(i.type)} data-testid={`connect-${i.type}-button`} className="px-3 py-1.5 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-[12px] font-semibold whitespace-nowrap">
                      Connect
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </TabsContent>
        <TabsContent value="punchout" className="mt-5">
          <PunchoutSettingsPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}
